import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, Spinner } from "@/components/ui-parts";

const EV_STATUSES = ["Missing", "Provided", "Validated", "Rejected"];

export default function EvidencePage() {
  const { user } = useAuth();
  const { withScope, activeTenantId } = useTenant();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", domain: "", status: "" });
  const canEdit = user?.role !== "viewer";

  const load = () => {
    setLoading(true);
    api.get("/requirements", { params: withScope() })
      .then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenantId]);

  const domains = useMemo(() => [...new Set(rows.map((r) => r.domain))].sort(), [rows]);
  const filtered = rows.filter((r) =>
    (!f.q || JSON.stringify(r).toLowerCase().includes(f.q.toLowerCase()))
    && (!f.domain || r.domain === f.domain)
    && (!f.status || r.evidenceStatus === f.status)
  );

  const update = async (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    try { await api.patch(`/requirements/${id}`, { [field]: value }); } catch (e) { alert(formatApiError(e)); load(); }
  };

  return (
    <div className="p-8 max-w-[1800px]" data-testid="evidence-page">
      <PageHeader eyebrow="Assessment" title="Evidence Register" description="Track evidence links, notes, ownership and auditor validation across all requirements." />

      <div className="card p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input placeholder="Search evidence..." value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="ev-search" />
          <select value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} data-testid="ev-domain">
            <option value="">All Domains</option>{domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="ev-status">
            <option value="">All Statuses</option>{EV_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 self-center">
            Shown: {filtered.length} / {rows.length}
          </div>
        </div>
      </div>

      <Section>
        {loading ? <div className="p-8"><Spinner /></div> : (
          <div className="overflow-x-auto" style={{ maxHeight: "70vh" }}>
            <table className="data-table">
              <thead><tr>
                <th>Req ID</th><th>Domain</th><th>Capability</th><th>Expected Evidence</th>
                <th>Link / Ref</th><th>Notes</th><th>Owner</th><th>Status</th><th>Auditor Comment</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.requirementId}</td>
                    <td>{r.domain}</td>
                    <td>{r.capability}</td>
                    <td className="text-slate-400 max-w-xs">{r.expectedEvidence}</td>
                    <td><input disabled={!canEdit} value={r.evidenceLink||""} onChange={(e)=>update(r.id,"evidenceLink",e.target.value)} style={{minWidth:140}} /></td>
                    <td><textarea disabled={!canEdit} rows={2} value={r.evidenceNotes||""} onChange={(e)=>update(r.id,"evidenceNotes",e.target.value)} style={{minWidth:160}} /></td>
                    <td><input disabled={!canEdit} value={r.evidenceOwner||""} onChange={(e)=>update(r.id,"evidenceOwner",e.target.value)} style={{minWidth:100}} /></td>
                    <td>
                      <select disabled={!canEdit} value={r.evidenceStatus||"Missing"} onChange={(e)=>update(r.id,"evidenceStatus",e.target.value)}>
                        {EV_STATUSES.map((s)=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td><textarea disabled={!canEdit} rows={2} value={r.auditorComment||""} onChange={(e)=>update(r.id,"auditorComment",e.target.value)} style={{minWidth:160}} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
